import React from 'react';
import { Link } from 'react-router-dom';
import { FaRobot, FaFileAlt, FaEye, FaDownload, FaCloud, FaCheckCircle } from 'react-icons/fa';

const features = [
  {
    icon: <FaRobot className="w-full h-full" />,
    title: "AI-Powered Content Generation",
    description: "Leverage Google's Gemini AI to create compelling resumes and cover letters tailored to your industry.",
    startColor: "#4F46E5",
    endColor: "#2563EB"
  },
  {
    icon: <FaFileAlt className="w-full h-full" />,
    title: "Professional Templates",
    description: "Choose from a variety of ATS-friendly templates designed by HR professionals.",
    startColor: "#9333EA",
    endColor: "#DB2777"
  },
  {
    icon: <FaEye className="w-full h-full" />,
    title: "Real-time Preview",
    description: "See your changes instantly with our live preview feature.",
    startColor: "#059669",
    endColor: "#10B981"
  },
  {
    icon: <FaDownload className="w-full h-full" />,
    title: "Quick Export",
    description: "Export your documents in multiple formats including PDF and Word, ready for submission.",
    startColor: "#EA580C",
    endColor: "#DC2626"
  },
  {
    icon: <FaCloud className="w-full h-full" />,
    title: "Cloud Storage",
    description: "Securely save and access your documents from anywhere.",
    startColor: "#0891B2",
    endColor: "#2563EB"
  },
  {
    icon: <FaCheckCircle className="w-full h-full" />,
    title: "ATS Optimized",
    description: "Ensure your resume passes Applicant Tracking Systems with our optimized templates.",
    startColor: "#D97706",
    endColor: "#F59E0B"
  }
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 bg-gradient-to-b from-[#0A0A0A] to-[#121212]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 animate-fade-in leading-tight">
              Create Professional Resumes & Cover Letters
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-6 md:mb-8 animate-fade-in-delay px-4 md:px-0">
              Build impressive resumes and cover letters in minutes with our AI-powered platform.
              Stand out from the crowd and land your dream job.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 px-4 md:px-0">
              <Link
                to="/resume-builder"
                className="w-full sm:w-auto px-6 md:px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/25 text-center"
              >
                Create Resume
              </Link>
              <Link
                to="/cover-letter-builder"
                className="w-full sm:w-auto px-6 md:px-8 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/25 text-center"
              >
                Write Cover Letter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 px-4 md:px-0">
              Powerful Features for Your Success
            </h2>
            <div className="w-16 md:w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card gradient-border relative p-6 rounded-2xl bg-[#1A1A1A] transition-all duration-500 overflow-hidden group"
              >
                <div className="relative z-10">
                  <div 
                    className="feature-icon w-16 h-16 rounded-2xl p-3 mb-6 text-white"
                    style={{
                      background: `linear-gradient(135deg, ${feature.startColor}, ${feature.endColor})`
                    }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="feature-title text-xl font-semibold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="feature-description text-gray-400 leading-relaxed text-sm">
                    {feature.description}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="progress-bar mt-6 h-1 w-full bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="progress-fill h-full bg-gradient-to-r"
                      style={{
                        '--start-color': feature.startColor,
                        '--end-color': feature.endColor,
                        background: `linear-gradient(to right, var(--start-color), var(--end-color))`
                      }}
                    />
                  </div>
                </div>
                <div 
                  className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"
                />
                <div 
                  className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"
                  style={{
                    '--start-color': feature.startColor,
                    '--end-color': feature.endColor,
                    background: `linear-gradient(to right, var(--start-color), var(--end-color))`
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#121212] to-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6 px-4 md:px-0">
              Ready to Build Your Professional Profile?
            </h2>
            <p className="text-lg md:text-xl text-gray-300 mb-6 md:mb-8 px-4 md:px-0">
              Join thousands of job seekers who have successfully landed their dream jobs using our platform.
            </p>
            <Link
              to="/signup"
              className="inline-block w-full sm:w-auto px-6 md:px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/25 mx-4 sm:mx-0"
            >
              Get Started Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
